import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { fetchErrorLogs, getErrorLogStats, resolveError } from '../../../services/errorLogService';
import { useAuth } from '../../../contexts/AuthContext';
import { format } from 'date-fns';

const ErrorLogViewer = ({ onClose }) => {
  const { user } = useAuth();
  const [logs, setLogs] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    errorSource: '',
    errorSeverity: '',
    componentName: '',
    resolved: false,
    dateFrom: '',
    dateTo: '',
    limit: 100
  });
  const [expandedLog, setExpandedLog] = useState(null);
  const [resolvingLog, setResolvingLog] = useState(null);
  const [resolutionNotes, setResolutionNotes] = useState('');

  useEffect(() => {
    loadErrorLogs();
    loadStats();
  }, [filters]);

  const loadErrorLogs = async () => {
    try {
      setLoading(true);
      const { data, error } = await fetchErrorLogs(filters);
      if (error) {
        console.error('Error loading error logs:', error);
      } else {
        setLogs(data || []);
      }
    } catch (error) {
      console.error('Exception loading error logs:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadStats = async () => {
    try {
      const { data, error } = await getErrorLogStats(filters);
      if (!error) {
        setStats(data);
      }
    } catch (error) {
      console.error('Exception loading stats:', error);
    }
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const handleResetFilters = () => {
    setFilters({
      errorSource: '',
      errorSeverity: '',
      componentName: '',
      resolved: false,
      dateFrom: '',
      dateTo: '',
      limit: 100
    });
  };

  const handleResolveError = async (logId) => {
    try {
      const { error } = await resolveError(logId, {
        resolvedBy: user?.email || 'admin@gridbalance.de',
        resolutionNotes: resolutionNotes
      });

      if (!error) {
        await loadErrorLogs();
        await loadStats();
        setResolvingLog(null);
        setResolutionNotes('');
      }
    } catch (error) {
      console.error('Error resolving error log:', error);
    }
  };

  const getSeverityColor = (severity) => {
    switch (severity) {
      case 'critical':
        return 'text-red-600 bg-red-50 border-red-200';
      case 'high':
        return 'text-orange-600 bg-orange-50 border-orange-200';
      case 'medium':
        return 'text-yellow-600 bg-yellow-50 border-yellow-200';
      case 'low':
      default:
        return 'text-blue-600 bg-blue-50 border-blue-200';
    }
  };

  const getSourceIcon = (source) => {
    switch (source) {
      case 'supabase_query':
        return 'Database';
      case 'supabase_auth':
        return 'Lock';
      case 'supabase_storage':
        return 'HardDrive';
      case 'api_call':
        return 'Globe';
      case 'client_error':
        return 'AlertCircle';
      case 'server_error':
        return 'Server';
      default:
        return 'AlertTriangle';
    }
  };

  const formatDate = (dateString) => {
    try {
      return format(new Date(dateString), 'dd.MM.yyyy HH:mm:ss');
    } catch {
      return dateString;
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-card rounded-lg shadow-xl border border-border w-full max-w-7xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="p-6 border-b border-border flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-heading font-semibold text-foreground flex items-center gap-2">
              <Icon name="AlertTriangle" size={24} />
              Fehlerprotokoll-System
            </h2>
            <p className="text-sm text-muted-foreground mt-1">
              Vollständige Erfassung von Supabase-Fehlern, Stack-Traces und Request-Payloads für Debugging
            </p>
          </div>
          <Button variant="ghost" size="sm" onClick={onClose}>
            <Icon name="X" size={16} />
          </Button>
        </div>

        {/* Stats Summary */}
        {stats && (
          <div className="p-6 border-b border-border bg-muted/30">
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
              <div className="bg-card p-4 rounded-lg border border-border">
                <p className="text-sm text-muted-foreground">Gesamt-Fehler</p>
                <p className="text-2xl font-bold text-foreground">{stats?.total}</p>
              </div>
              <div className="bg-card p-4 rounded-lg border border-border">
                <p className="text-sm text-muted-foreground">Kritisch</p>
                <p className="text-2xl font-bold text-red-600">{stats?.bySeverity?.critical || 0}</p>
              </div>
              <div className="bg-card p-4 rounded-lg border border-border">
                <p className="text-sm text-muted-foreground">Hoch</p>
                <p className="text-2xl font-bold text-orange-600">{stats?.bySeverity?.high || 0}</p>
              </div>
              <div className="bg-card p-4 rounded-lg border border-border">
                <p className="text-sm text-muted-foreground">Ungelöst</p>
                <p className="text-2xl font-bold text-yellow-600">{stats?.unresolved || 0}</p>
              </div>
              <div className="bg-card p-4 rounded-lg border border-border">
                <p className="text-sm text-muted-foreground">Gelöst</p>
                <p className="text-2xl font-bold text-green-600">{stats?.resolved || 0}</p>
              </div>
            </div>
          </div>
        )}

        {/* Filters */}
        <div className="p-6 border-b border-border bg-muted/10">
          <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-4">
            <select
              value={filters?.errorSource}
              onChange={(e) => handleFilterChange('errorSource', e?.target?.value)}
              className="px-3 py-2 border border-border rounded-lg bg-background text-foreground"
            >
              <option value="">Alle Quellen</option>
              <option value="supabase_query">Supabase Query</option>
              <option value="supabase_auth">Supabase Auth</option>
              <option value="supabase_storage">Supabase Storage</option>
              <option value="api_call">API Call</option>
              <option value="client_error">Client Error</option>
              <option value="server_error">Server Error</option>
            </select>

            <select
              value={filters?.errorSeverity}
              onChange={(e) => handleFilterChange('errorSeverity', e?.target?.value)}
              className="px-3 py-2 border border-border rounded-lg bg-background text-foreground"
            >
              <option value="">Alle Schweregrade</option>
              <option value="low">Niedrig</option>
              <option value="medium">Mittel</option>
              <option value="high">Hoch</option>
              <option value="critical">Kritisch</option>
            </select>

            <select
              value={filters?.resolved}
              onChange={(e) => handleFilterChange('resolved', e?.target?.value === 'true')}
              className="px-3 py-2 border border-border rounded-lg bg-background text-foreground"
            >
              <option value="false">Ungelöst</option>
              <option value="true">Gelöst</option>
            </select>

            <input
              type="date"
              value={filters?.dateFrom}
              onChange={(e) => handleFilterChange('dateFrom', e?.target?.value)}
              className="px-3 py-2 border border-border rounded-lg bg-background text-foreground"
              placeholder="Von Datum"
            />

            <input
              type="date"
              value={filters?.dateTo}
              onChange={(e) => handleFilterChange('dateTo', e?.target?.value)}
              className="px-3 py-2 border border-border rounded-lg bg-background text-foreground"
              placeholder="Bis Datum"
            />

            <Button
              variant="outline"
              size="sm"
              iconName="RotateCcw"
              onClick={handleResetFilters}
            >
              Zurücksetzen
            </Button>
          </div>
        </div>

        {/* Logs Table */}
        <div className="flex-1 overflow-auto p-6">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
            </div>
          ) : logs?.length === 0 ? (
            <div className="text-center py-12">
              <Icon name="CheckCircle" size={48} className="mx-auto text-green-500 mb-4" />
              <p className="text-muted-foreground">Keine Fehler gefunden - Alles läuft perfekt!</p>
            </div>
          ) : (
            <div className="space-y-3">
              {logs?.map((log) => (
                <div
                  key={log?.id}
                  className="bg-card border border-border rounded-lg p-4 hover:shadow-md transition-shadow"
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex items-start gap-3 flex-1">
                      <div className="mt-1">
                        <Icon name={getSourceIcon(log?.errorSource)} size={20} className="text-primary" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap mb-2">
                          <span className={`px-2 py-1 rounded text-xs font-medium border ${getSeverityColor(log?.errorSeverity)}`}>
                            {log?.errorSeverity?.toUpperCase()}
                          </span>
                          <span className="text-xs text-muted-foreground">
                            {formatDate(log?.createdAt)}
                          </span>
                          {log?.userEmail && (
                            <span className="text-xs text-muted-foreground">
                              User: {log?.userEmail}
                            </span>
                          )}
                          {log?.componentName && (
                            <span className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded">
                              {log?.componentName}
                            </span>
                          )}
                          {log?.resolved && (
                            <span className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded flex items-center gap-1">
                              <Icon name="CheckCircle" size={12} />
                              Gelöst
                            </span>
                          )}
                        </div>
                        <p className="text-sm font-medium text-foreground mb-1">
                          {log?.errorMessage}
                        </p>
                        {log?.errorCode && (
                          <p className="text-xs text-muted-foreground">
                            Code: {log?.errorCode}
                          </p>
                        )}
                        {log?.functionName && (
                          <p className="text-xs text-muted-foreground">
                            Funktion: {log?.functionName}
                          </p>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {!log?.resolved && (
                        <Button
                          variant="outline"
                          size="sm"
                          iconName="Check"
                          onClick={() => setResolvingLog(log?.id)}
                        >
                          Lösen
                        </Button>
                      )}
                      <button
                        onClick={() => setExpandedLog(expandedLog === log?.id ? null : log?.id)}
                        className="p-2 hover:bg-muted rounded"
                      >
                        <Icon
                          name={expandedLog === log?.id ? 'ChevronUp' : 'ChevronDown'}
                          size={20}
                          className="text-muted-foreground"
                        />
                      </button>
                    </div>
                  </div>

                  {/* Expanded Details */}
                  {expandedLog === log?.id && (
                    <div className="mt-4 pt-4 border-t border-border space-y-3">
                      {log?.errorDetails && (
                        <div>
                          <p className="text-xs font-semibold text-foreground mb-1">Details:</p>
                          <p className="text-xs text-muted-foreground bg-muted p-2 rounded">{log?.errorDetails}</p>
                        </div>
                      )}
                      {log?.errorHint && (
                        <div>
                          <p className="text-xs font-semibold text-foreground mb-1">Hinweis:</p>
                          <p className="text-xs text-muted-foreground bg-muted p-2 rounded">{log?.errorHint}</p>
                        </div>
                      )}
                      {log?.stackTrace && (
                        <div>
                          <p className="text-xs font-semibold text-foreground mb-1">Stack Trace:</p>
                          <pre className="text-xs text-muted-foreground bg-muted p-2 rounded overflow-x-auto max-h-40">
                            {log?.stackTrace}
                          </pre>
                        </div>
                      )}
                      {log?.requestPayload && (
                        <div>
                          <p className="text-xs font-semibold text-foreground mb-1">Request Payload:</p>
                          <pre className="text-xs text-muted-foreground bg-muted p-2 rounded overflow-x-auto max-h-40">
                            {JSON.stringify(log?.requestPayload, null, 2)}
                          </pre>
                        </div>
                      )}
                      {log?.fullErrorObject && (
                        <div>
                          <p className="text-xs font-semibold text-foreground mb-1">Vollständiges Error-Objekt:</p>
                          <pre className="text-xs text-muted-foreground bg-muted p-2 rounded overflow-x-auto max-h-40">
                            {JSON.stringify(log?.fullErrorObject, null, 2)}
                          </pre>
                        </div>
                      )}
                      {log?.browserInfo && (
                        <div>
                          <p className="text-xs font-semibold text-foreground mb-1">Browser Info:</p>
                          <pre className="text-xs text-muted-foreground bg-muted p-2 rounded overflow-x-auto">
                            {JSON.stringify(log?.browserInfo, null, 2)}
                          </pre>
                        </div>
                      )}
                      {log?.resolved && log?.resolutionNotes && (
                        <div>
                          <p className="text-xs font-semibold text-foreground mb-1">Lösungsnotizen:</p>
                          <p className="text-xs text-muted-foreground bg-green-50 p-2 rounded">{log?.resolutionNotes}</p>
                          <p className="text-xs text-muted-foreground mt-1">
                            Gelöst von: {log?.resolvedBy} am {formatDate(log?.resolvedAt)}
                          </p>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Resolution Form */}
                  {resolvingLog === log?.id && (
                    <div className="mt-4 pt-4 border-t border-border">
                      <p className="text-sm font-semibold text-foreground mb-2">Fehler als gelöst markieren</p>
                      <textarea
                        value={resolutionNotes}
                        onChange={(e) => setResolutionNotes(e?.target?.value)}
                        placeholder="Lösungsnotizen eingeben..."
                        className="w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground mb-2"
                        rows={3}
                      />
                      <div className="flex gap-2">
                        <Button
                          variant="primary"
                          size="sm"
                          iconName="Check"
                          onClick={() => handleResolveError(log?.id)}
                        >
                          Als gelöst markieren
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            setResolvingLog(null);
                            setResolutionNotes('');
                          }}
                        >
                          Abbrechen
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ErrorLogViewer;